import React, { useRef, useState } from "react";
import { CheckCircle, XCircle, Star, Info, X } from "lucide-react";
import { RateAnalysis, QuoteRequest } from "../../types/rate-analysis";
import SpeakToProfessionalModal from "../SpeakToProfessionalModal";
import {
  calculateTotalMonthlyPayment,
  calculateMonthlyPayment,
} from "../../utils/mortgageCalculations";
import { Tooltip } from "react-tooltip";
import { formatCurrency, formatRate } from "../../utils/numberFormatting";
import ThirdPartyFees from "../ThirdPartyFees";
import {
  calculateThirdPartyFees,
  calculateThirdPartyFeesAmount,
} from "../../utils/thirdPartyFees";
import { isPurchaseRequest } from "../../utils/quoteRequestUtils";
import { MONTHLY_PAYMENT_TOOLTIP } from "../../constants/tooltips";
import Confetti from "../ui/Confetti";

interface RefinanceComparisonReportProps {
  analysis: RateAnalysis;
  quoteRequest: QuoteRequest | undefined;
  gradeTitle?: string;
  onRequestUpdate?: () => void;
}

interface DisplayQuote {
  rate: number;
  lenderFees: number;
  mortgageInsurance: number;
  isUserQuote: boolean;
  isRecommended: boolean;
  lenderName: string;
  monthlyPayment: number;
  monthlyPaymentPI: number; // P&I only (no PMI)
  apr?: number;
  loanProgram?: string;
}

interface LoanInformationModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteRequest: QuoteRequest | undefined;
}

const LoanInformationModal: React.FC<LoanInformationModalProps> = ({
  isOpen,
  onClose,
  quoteRequest,
}) => {
  if (!isOpen || !quoteRequest) return null;

  const info = [
    {
      label: "Loan Type",
      value: isPurchaseRequest(quoteRequest) ? "Purchase" : "Refinance",
    },
    { label: "Property Type", value: quoteRequest.property_type },
    { label: "Property Style", value: quoteRequest.property_style },
    {
      label: "Military Service",
      value: quoteRequest.military_service ? "Yes" : "No",
    },
    { label: "Credit Score", value: quoteRequest.credit_score },
    { label: "Zip Code", value: quoteRequest.zip_code },
  ];

  if (isPurchaseRequest(quoteRequest)) {
    info.push(
      {
        label: "Purchase Price",
        value: `$${quoteRequest.purchase_price?.toLocaleString()}`,
      },
      {
        label: "Down Payment",
        value: `$${quoteRequest.down_payment?.toLocaleString()}`,
      },
      { label: "First Home", value: quoteRequest.is_first_home ? "Yes" : "No" }
    );
  } else {
    info.push(
      {
        label: "Remaining Balance",
        value: `$${quoteRequest.remaining_balance?.toLocaleString()}`,
      },
      { label: "Cash Out", value: quoteRequest.cash_out ? "Yes" : "No" },
      ...(quoteRequest.cash_out
        ? [
            {
              label: "Cash Out Amount",
              value: `$${quoteRequest.cash_out_amount?.toLocaleString()}`,
            },
          ]
        : [])
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 animate-fade-in">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Loan Information
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-3">
          {info.map((item, index) => (
            <div
              key={index}
              className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
            >
              <span className="text-gray-600 font-medium">{item.label}</span>
              <span className="text-gray-900">{item.value}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const RefinanceComparisonReport: React.FC<RefinanceComparisonReportProps> = ({
  analysis,
  quoteRequest,
  gradeTitle = "Quote Grade",
  onRequestUpdate,
}) => {
  const [selectedQuote, setSelectedQuote] = React.useState<DisplayQuote | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isLoanInfoModalOpen, setIsLoanInfoModalOpen] = React.useState(false);
  const [selectedPartnerIndex, setSelectedPartnerIndex] = React.useState<
    number | null
  >(null);
  const openedByButtonRef = useRef(false);
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(
    null
  );
  const [showConfetti, setShowConfetti] = useState(false);
  const hasShownConfettiRef = useRef(false);

  if (!quoteRequest) {
    return null;
  }

  // Determine if any partner quote has APR
  // const showAprColumn = quoteRequest.quotes?.some(
  //   (q) => typeof q.apr === "number" && !isNaN(q.apr)
  // );

  // Calculate loan amount
  const loanAmount =
    quoteRequest.loan_type === "purchase"
      ? (quoteRequest.purchase_price || 0) - (quoteRequest.down_payment || 0)
      : quoteRequest.remaining_balance || 0;

  // All quotes are partner quotes
  const partnerQuotes: DisplayQuote[] = quoteRequest.quotes?.length
    ? quoteRequest.quotes
        .sort((a, b) => a.rate - b.rate)
        .map((quote) => ({
          rate: quote.rate,
          lenderFees: quote.lenderFees,
          mortgageInsurance: quote.mortgageInsurance || 0,
          isUserQuote: false,
          isRecommended: quote.isRecommended || false,
          lenderName: "Keystone Financial Services, LLC",
          monthlyPayment: calculateTotalMonthlyPayment(
            loanAmount,
            quote.rate,
            360, // Assuming 30-year term
            quote.mortgageInsurance || 0
          ),
          monthlyPaymentPI: calculateMonthlyPayment(
            loanAmount,
            quote.rate,
            360 // Assuming 30-year term
          ),
          apr: quote.apr,
        }))
    : [];

  const hasBrokerRate = !!quoteRequest.broker_rate;
  const hasRates = partnerQuotes.length > 0;
  const hasMortgageInsurance = partnerQuotes.some(
    (q) => q.mortgageInsurance > 0
  );

  // Calculate refinance recommendation data
  let refinanceData = null;
  if (hasRates && quoteRequest.current_mortgage) {
    const currentPayment =
      quoteRequest.current_mortgage.calculated_monthly_payment_pi ||
      quoteRequest.current_mortgage.monthly_payment;

    const currentRate = quoteRequest.current_mortgage.rate;
    const thirdPartyFees = calculateThirdPartyFees(
      loanAmount,
      "refinance",
      analysis.lender || "rocket"
    );
    const thirdPartyFeesAmount = calculateThirdPartyFeesAmount(thirdPartyFees);

    const lowestRateQuote = partnerQuotes[0];
    const bestQuote = partnerQuotes.find(
      (quote) =>
        quote.rate < currentRate &&
        quote.lenderFees < 0 &&
        Math.abs(quote.lenderFees) > thirdPartyFeesAmount
    );

    // Calculate monthly savings based on the best quote (if available) or lowest rate quote
    const bestRefiPayment =
      bestQuote?.monthlyPaymentPI || partnerQuotes[0]?.monthlyPaymentPI || 0;
    const monthlySavings =
      Math.floor((currentPayment - bestRefiPayment) / 5) * 5;

    const isRecommended =
      bestQuote &&
      bestQuote.rate < currentRate &&
      bestQuote.lenderFees < 0 &&
      Math.abs(bestQuote.lenderFees) > thirdPartyFeesAmount;

    refinanceData = {
      currentPayment,
      bestRefiPayment,
      monthlySavings,
      currentRate,
      thirdPartyFees,
      thirdPartyFeesAmount,
      lowestRateQuote,
      bestQuote,
      isRecommended,
    };
  }

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedQuote(null);
    setSelectedPartnerIndex(null);
  };

  // Check if we should show confetti (only once when refinancing is recommended)
  if (refinanceData?.isRecommended && !hasShownConfettiRef.current) {
    hasShownConfettiRef.current = true;
    setShowConfetti(true);

    // Hide confetti after animation completes
    setTimeout(() => {
      setShowConfetti(false);
    }, 3500);
  }

  return (
    <>
      <Confetti isActive={showConfetti} duration={3000} />
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Grade Display - Only show if there's a broker rate */}
        {hasBrokerRate && analysis.grade && (
          <div className="text-center mb-6">
            <div className="text-lg font-semibold text-gray-900 mb-2">
              {gradeTitle}
            </div>
            <div
              className={`
            inline-block px-6 py-2 rounded-full text-white text-4xl font-bold
            ${analysis.grade === "A" ? "bg-green-600" : ""}
            ${
              analysis.grade === "B" || analysis.grade === "C"
                ? "bg-yellow-500"
                : ""
            }
            ${
              analysis.grade === "D" || analysis.grade === "F"
                ? "bg-red-600"
                : ""
            }
          `}
            >
              {analysis.grade}
            </div>
          </div>
        )}

        {/* Refinance Recommendation Header */}
        {refinanceData && (
          <div className="bg-gradient-to-r from-primary-light/5 to-secondary-light/5 border border-primary-light/20 rounded-xl p-6 mb-6">
            <div className="text-center">
              {refinanceData.isRecommended ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-8 w-8 text-success" />
                    <h2 className="text-2xl font-heading font-bold text-gray-900">
                      Refinancing is Recommended!
                    </h2>
                  </div>
                  <div className="text-lg text-gray-700">
                    <span className="font-semibold">
                      You could save{" "}
                      <span className="text-green-600 font-bold">
                        ${formatCurrency(refinanceData.monthlySavings)}
                      </span>{" "}
                      per month
                    </span>
                    {refinanceData.currentRate >
                      (refinanceData.bestQuote?.rate || 0) && (
                      <span className="block text-gray-600 mt-1">
                        by refinancing from{" "}
                        {formatRate(refinanceData.currentRate)}% to{" "}
                        {formatRate(refinanceData.bestQuote?.rate || 0)}%
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 bg-white/50 rounded-lg px-4 py-2 inline-block">
                    You can refinance now with no cost to you. The $
                    {formatCurrency(
                      Math.abs(refinanceData.bestQuote?.lenderFees || 0)
                    )}{" "}
                    lender credit will cover the third party fees.
                  </div>
                </div>
              ) : refinanceData.monthlySavings > 0 &&
                (refinanceData.bestQuote || refinanceData.lowestRateQuote) ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center space-x-2">
                    <Info className="h-8 w-8 text-primary" />
                    <h2 className="text-2xl font-heading font-bold text-gray-900">
                      Potential Savings Available
                    </h2>
                  </div>
                  <div className="text-lg text-gray-700">
                    <span className="font-semibold">
                      You could save{" "}
                      <span className="text-green-600 font-bold">
                        ${formatCurrency(refinanceData.monthlySavings)}
                      </span>{" "}
                      per month
                    </span>
                    {(refinanceData.bestQuote ||
                      refinanceData.lowestRateQuote) &&
                      refinanceData.currentRate >
                        (
                          refinanceData.bestQuote ||
                          refinanceData.lowestRateQuote
                        ).rate && (
                        <span className="block text-gray-600 mt-1">
                          by refinancing from{" "}
                          {formatRate(refinanceData.currentRate)}% to{" "}
                          {formatRate(
                            (
                              refinanceData.bestQuote ||
                              refinanceData.lowestRateQuote
                            ).rate
                          )}
                          %
                        </span>
                      )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center space-x-2">
                    <XCircle className="h-8 w-8 text-error" />
                    <h2 className="text-2xl font-heading font-bold text-gray-900">
                      Wait to Refinance
                    </h2>
                  </div>
                  <div className="text-lg text-gray-700">
                    <span className="font-semibold">
                      You wouldn't save money by refinancing today.
                    </span>
                    <span className="block text-gray-600 mt-1">
                      Your current {formatRate(refinanceData.currentRate)}% rate
                      is competitive
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Refinance Quotes Comparison */}
        {hasRates && (
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Refinance Options:{" "}
                {partnerQuotes[0]?.loanProgram || "30-Year Fixed"}
              </h3>
              <button
                onClick={() => setIsLoanInfoModalOpen(true)}
                className="btn-tertiary"
              >
                View Loan Scenario
              </button>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-6">
              {/* Partner Quotes */}
              {partnerQuotes.map((quote, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-900">
                      {quote.lenderName}
                    </div>
                  </div>
                  <div className="space-y-3 pl-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24">
                        Interest Rate:
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-gray-900">
                          {formatRate(quote.rate)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24">
                        Monthly Payment (P&I):
                      </span>
                      <span className="text-gray-700">
                        ${formatCurrency(quote.monthlyPaymentPI)}
                      </span>
                    </div>
                    {hasMortgageInsurance && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500 w-24">
                          PMI:
                        </span>
                        <span className="text-gray-700">
                          {(() => {
                            const pmi = quote.mortgageInsurance;
                            const isPurchase =
                              quoteRequest.loan_type === "purchase";
                            const downPaymentPercent =
                              isPurchase &&
                              quoteRequest.purchase_price &&
                              quoteRequest.down_payment
                                ? (quoteRequest.down_payment /
                                    quoteRequest.purchase_price) *
                                  100
                                : null;

                            if (pmi === null || pmi === undefined) {
                              if (
                                isPurchase &&
                                downPaymentPercent !== null &&
                                downPaymentPercent < 20
                              ) {
                                return (
                                  <span className="text-gray-500">
                                    Not provided
                                  </span>
                                );
                              }
                              return <span>$0</span>;
                            }

                            return <span>${formatCurrency(pmi)}</span>;
                          })()}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24">
                        Monthly Payment:
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-700">
                          $
                          {formatCurrency(
                            typeof quote.monthlyPayment === "number"
                              ? quote.monthlyPayment
                              : 0
                          )}
                        </span>
                        <Info
                          size={14}
                          className="text-gray-400 cursor-help"
                          data-tooltip-id="monthly-payment-tooltip-mobile"
                          data-tooltip-content={MONTHLY_PAYMENT_TOOLTIP}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 w-24">
                        Lender Fees / (
                        <span className="text-green-500">Credit</span>¹)
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-700">
                          {(() => {
                            const fees = quote.lenderFees;
                            if (fees === null || fees === undefined) {
                              return (
                                <span className="text-gray-500">
                                  Not provided
                                </span>
                              );
                            }
                            const numericFees =
                              typeof fees === "string"
                                ? parseFloat(fees)
                                : fees;
                            return numericFees < 0 ? (
                              <span className="text-green-500">
                                ${formatCurrency(Math.abs(numericFees))}
                              </span>
                            ) : (
                              <span>${formatCurrency(fees)}</span>
                            );
                          })()}
                        </span>
                        <Info
                          size={14}
                          className="text-gray-400 cursor-help"
                          data-tooltip-id="lender-fees-tooltip-mobile"
                          data-tooltip-content="Lender credits reduce your closing costs. Negative values indicate credits (money back to you)."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Interest Rate
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Monthly Payment (P&I)
                      <Info
                        size={14}
                        className="text-gray-400 ml-1 inline-block cursor-help"
                        data-tooltip-id="monthly-payment-pi-tooltip"
                        data-tooltip-content="Monthly payment for principal and interest only, excluding PMI and other costs."
                      />
                    </th>
                    {hasMortgageInsurance && (
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        PMI
                      </th>
                    )}
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Monthly Payment
                      <Info
                        size={14}
                        className="text-gray-400 ml-1 inline-block cursor-help"
                        data-tooltip-id="monthly-payment-tooltip"
                        data-tooltip-content={MONTHLY_PAYMENT_TOOLTIP}
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Lender Fees / (
                      <span className="text-green-500">Credit</span>¹)
                      <Info
                        size={14}
                        className="text-gray-400 ml-1 inline-block cursor-help"
                        data-tooltip-id="lender-fees-tooltip"
                        data-tooltip-content="Lender credits reduce your closing costs. Negative values indicate credits (money back to you)."
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500"></th>
                  </tr>
                </thead>
                <tbody>
                  {/* All Partner Quotes */}
                  {partnerQuotes.length > 0 && (
                    <>
                      <tr onMouseLeave={() => setHoveredRowIndex(null)}>
                        <td
                          className={`py-3 px-4 text-lg font-semibold text-gray-900 cursor-pointer${
                            hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(0)}
                          onClick={() => {
                            setSelectedPartnerIndex(0);
                            setSelectedQuote(partnerQuotes[0]);
                            setIsModalOpen(true);
                          }}
                        >
                          <span className="inline-flex items-center">
                            {formatRate(partnerQuotes[0].rate)}%
                            {partnerQuotes[0].isRecommended && (
                              <span className="ml-1">
                                <Star
                                  size={16}
                                  className="text-primary inline"
                                />
                                <span className="sr-only">
                                  Recommended Option
                                </span>
                              </span>
                            )}
                          </span>
                        </td>
                        <td
                          className={`py-3 px-4 text-gray-700 cursor-pointer${
                            hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(0)}
                          onClick={() => {
                            setSelectedPartnerIndex(0);
                            setSelectedQuote(partnerQuotes[0]);
                            setIsModalOpen(true);
                          }}
                        >
                          ${formatCurrency(partnerQuotes[0].monthlyPaymentPI)}
                        </td>
                        {hasMortgageInsurance && (
                          <td
                            className={`py-3 px-4 text-gray-700 cursor-pointer${
                              hoveredRowIndex === 0
                                ? " bg-primary-light/10"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredRowIndex(0)}
                            onClick={() => {
                              setSelectedPartnerIndex(0);
                              setSelectedQuote(partnerQuotes[0]);
                              setIsModalOpen(true);
                            }}
                          >
                            $
                            {formatCurrency(
                              typeof partnerQuotes[0].mortgageInsurance ===
                                "number"
                                ? partnerQuotes[0].mortgageInsurance
                                : 0
                            )}
                          </td>
                        )}
                        <td
                          className={`py-3 px-4 text-gray-700 cursor-pointer${
                            hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(0)}
                          onClick={() => {
                            setSelectedPartnerIndex(0);
                            setSelectedQuote(partnerQuotes[0]);
                            setIsModalOpen(true);
                          }}
                        >
                          $
                          {formatCurrency(
                            typeof partnerQuotes[0].monthlyPayment === "number"
                              ? partnerQuotes[0].monthlyPayment
                              : 0
                          )}
                        </td>
                        <td
                          className={`py-3 px-4 text-gray-700 cursor-pointer${
                            hoveredRowIndex === 0 ? " bg-primary-light/10" : ""
                          }`}
                          onMouseEnter={() => setHoveredRowIndex(0)}
                          onClick={() => {
                            setSelectedPartnerIndex(0);
                            setSelectedQuote(partnerQuotes[0]);
                            setIsModalOpen(true);
                          }}
                        >
                          {(() => {
                            const fees =
                              typeof partnerQuotes[0].lenderFees === "string"
                                ? parseFloat(partnerQuotes[0].lenderFees)
                                : partnerQuotes[0].lenderFees;
                            return fees < 0 ? (
                              <span className="text-green-500">
                                ${formatCurrency(Math.abs(fees))}
                              </span>
                            ) : (
                              <span>${formatCurrency(fees)}</span>
                            );
                          })()}
                        </td>
                        <td
                          className={
                            hoveredRowIndex === 0 ? "bg-primary-light/10" : ""
                          }
                        ></td>
                      </tr>
                      {partnerQuotes.slice(1).map((quote, index) => (
                        <tr
                          key={index}
                          onMouseLeave={() => setHoveredRowIndex(null)}
                        >
                          <td
                            className={`py-3 px-4 text-lg font-semibold text-gray-900 cursor-pointer${
                              hoveredRowIndex === index + 1
                                ? " bg-primary-light/10"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredRowIndex(index + 1)}
                            onClick={() => {
                              setSelectedPartnerIndex(index + 1);
                              setSelectedQuote(quote);
                              setIsModalOpen(true);
                            }}
                          >
                            <span className="inline-flex items-center">
                              {formatRate(quote.rate)}%
                              {quote.isRecommended && (
                                <span className="ml-1">
                                  <Star
                                    size={16}
                                    className="text-primary inline"
                                  />
                                  <span className="sr-only">
                                    Recommended Option
                                  </span>
                                </span>
                              )}
                            </span>
                          </td>
                          <td
                            className={`py-3 px-4 text-gray-700 cursor-pointer${
                              hoveredRowIndex === index + 1
                                ? " bg-primary-light/10"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredRowIndex(index + 1)}
                            onClick={() => {
                              setSelectedPartnerIndex(index + 1);
                              setSelectedQuote(quote);
                              setIsModalOpen(true);
                            }}
                          >
                            ${formatCurrency(quote.monthlyPaymentPI)}
                          </td>
                          {hasMortgageInsurance && (
                            <td
                              className={`py-3 px-4 text-gray-700 cursor-pointer${
                                hoveredRowIndex === index + 1
                                  ? " bg-primary-light/10"
                                  : ""
                              }`}
                              onMouseEnter={() => setHoveredRowIndex(index + 1)}
                              onClick={() => {
                                setSelectedPartnerIndex(index + 1);
                                setSelectedQuote(quote);
                                setIsModalOpen(true);
                              }}
                            >
                              $
                              {formatCurrency(
                                typeof quote.mortgageInsurance === "number"
                                  ? quote.mortgageInsurance
                                  : 0
                              )}
                            </td>
                          )}
                          <td
                            className={`py-3 px-4 text-gray-700 cursor-pointer${
                              hoveredRowIndex === index + 1
                                ? " bg-primary-light/10"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredRowIndex(index + 1)}
                            onClick={() => {
                              setSelectedPartnerIndex(index + 1);
                              setSelectedQuote(quote);
                              setIsModalOpen(true);
                            }}
                          >
                            $
                            {formatCurrency(
                              typeof quote.monthlyPayment === "number"
                                ? quote.monthlyPayment
                                : 0
                            )}
                          </td>
                          <td
                            className={`py-3 px-4 text-gray-700 cursor-pointer${
                              hoveredRowIndex === index + 1
                                ? " bg-primary-light/10"
                                : ""
                            }`}
                            onMouseEnter={() => setHoveredRowIndex(index + 1)}
                            onClick={() => {
                              setSelectedPartnerIndex(index + 1);
                              setSelectedQuote(quote);
                              setIsModalOpen(true);
                            }}
                          >
                            {(() => {
                              const fees =
                                typeof quote.lenderFees === "string"
                                  ? parseFloat(quote.lenderFees)
                                  : quote.lenderFees;
                              return fees < 0 ? (
                                <span className="text-green-500">
                                  ${formatCurrency(Math.abs(fees))}
                                </span>
                              ) : (
                                <span>${formatCurrency(fees)}</span>
                              );
                            })()}
                          </td>
                          <td
                            className={
                              hoveredRowIndex === index + 1
                                ? "bg-primary-light/10"
                                : ""
                            }
                          ></td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
              <div className="flex justify-between items-center mt-4">
                {/* Rate Estimates - Custom styling for this section */}
                {onRequestUpdate && (
                  <div className="flex-1 max-w-md">
                    <div className="text-sm text-gray-600">
                      <span>
                        Rate estimates as of{" "}
                        {new Date(
                          quoteRequest.created_at || new Date()
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                        .{" "}
                      </span>
                      <button
                        onClick={onRequestUpdate}
                        className="text-primary hover:text-primary-dark underline font-medium"
                      >
                        Request updated rates
                      </button>
                    </div>
                  </div>
                )}

                <button
                  className="btn-primary px-6 py-2 text-base font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed ml-4"
                  onClick={() => {
                    openedByButtonRef.current = true;
                    if (selectedPartnerIndex !== null) {
                      setSelectedQuote(partnerQuotes[selectedPartnerIndex]);
                    } else {
                      setSelectedQuote(null); // No row selected, open modal with dropdown
                    }
                    setIsModalOpen(true);
                  }}
                >
                  Contact Broker
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Third Party Fees and Notes - Side by side */}
        {hasRates && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
            {/* Third Party Fees */}
            <div>
              <ThirdPartyFees fees={refinanceData?.thirdPartyFees || []} />
            </div>

            {/* Notes Section */}
            {
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Notes
                </h3>
                <div className="space-y-2 text-sm">
                  {/* Standard notes (1 and 2) */}
                  <div className="flex gap-2">
                    <span className="font-medium text-gray-700">1.</span>
                    <span className="text-gray-600">
                      Lender credit to be applied towards closing costs (title
                      fees, appraisal, escrows).
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-gray-700">2.</span>
                    <span className="text-gray-600">
                      Title fees can be shopped for, and are the same no matter
                      who does your loan.
                    </span>
                  </div>

                  {/* Analysis notes with stars */}
                  {analysis.notes?.map((note, index) => (
                    <div key={index} className="flex gap-2">
                      <span className="font-medium text-gray-700">★</span>
                      <span className="text-gray-600">{note}</span>
                    </div>
                  ))}
                </div>
              </div>
            }
          </div>
        )}

        {/* Loan Terms Disclosure */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            <p className="font-medium mb-1 text-gray-600">
              Loan Terms & Finance Charges
            </p>
            <p>
              All quotes provided by Keystone Financial Services, LLC are for
              30-year conventional fixed-rate mortgages with 360 monthly
              payments. Total finance charges include lender fees and private
              mortgage insurance (PMI) if applicable. Monthly payments shown
              include principal, interest, and PMI only. Property taxes and
              homeowners insurance are not included.
            </p>
          </div>
        </div>

        <SpeakToProfessionalModal
          isOpen={isModalOpen}
          onClose={closeModal}
          quotes={partnerQuotes}
          selectedQuote={selectedQuote}
          showDropdown={openedByButtonRef.current && selectedQuote === null}
          question={
            selectedQuote
              ? `I am interested in the ${selectedQuote.rate}% rate with ${
                  selectedQuote.lenderFees < 0
                    ? `a credit of $${Math.abs(
                        selectedQuote.lenderFees
                      ).toLocaleString()} in lender fees`
                    : `$${selectedQuote.lenderFees.toLocaleString()} in lender fees`
                }.`
              : undefined
          }
        />
        <LoanInformationModal
          isOpen={isLoanInfoModalOpen}
          onClose={() => setIsLoanInfoModalOpen(false)}
          quoteRequest={quoteRequest}
        />
        <Tooltip id="monthly-payment-tooltip" className="max-w-sm" />
        <Tooltip id="monthly-payment-pi-tooltip" className="max-w-sm" />
        <Tooltip id="lender-fees-tooltip" className="max-w-sm" />
        <Tooltip id="apr-tooltip" className="max-w-sm" />
        <Tooltip id="monthly-payment-tooltip-mobile" className="max-w-sm" />
        <Tooltip id="lender-fees-tooltip-mobile" className="max-w-sm" />
        <Tooltip id="apr-tooltip-mobile" className="max-w-sm" />
      </div>
    </>
  );
};

export default RefinanceComparisonReport;
